import { binColor } from '@resistapp/client/components/plots/legends/heatmap-bins';
import { PlotTooltip, usePlotTooltip } from '@resistapp/client/components/tooltips/plot-tooltip';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { BarDatum, getBarDatumKeys } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { GeneGrouping } from '@resistapp/common/assays';
import { isAdmin } from '@resistapp/common/features';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { BarStack } from '@visx/shape';
import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { theme } from '../../shared/theme';
import type { KeysPressOptions } from '../legends/legend';
import { GeneBarStackProps, getGroupItemWidthAndMargin } from './data-groups';
import { ScaleBand, ScaleTime } from './multi-plot';
import { BarTooltipData, getAbundace, HeatmapTooltipContent, NumDetectedTooltipContent } from './tooltips';
import { getSampleSelector, getSemiTransparentColor, getVisualSelectionState, VisualSelectionStatus } from './utils';

export function GenesBarStack(props: GeneBarStackProps) {
  const {
    datum,
    geneGroups,
    xScale,
    yScale,
    groupWidth,
    groupLength,
    currentGroupLength,
    colorScale,
    isHeatmap,
    sortIdxByGene,
    totalGroupSize,
    bioRepCount,
    j,
    i,
    singleGeneHeatBoxHeight,
    againstTime,
  } = props;
  const { queryFilters, samplesBeingSelected, setSamplesBeingSelected, environmentsForSelectedEnvTypeGroup, project } =
    useResearchContext();
  const { user } = useUser();
  const { getGroup } = useAssayContext();

  const isAdminUser = isAdmin(user);
  const sampleById = useMemo(() => {
    const samplesByUID = project ? project.samplesByUID : {};
    return keyBy(flattenSamplesByUID(samplesByUID), s => s.id);
  }, [project]);

  const openChip = useCallback(
    (sampleId: number, assay: string) => {
      if (!isAdminUser) return;
      const sample = sampleById[sampleId];
      const chipId = sample.chipIds?.[0];
      if (!chipId) return;
      const base = `${sample.id}-${sample.number}${sample.bioRep}`;
      const samplesParam = [1, 2, 3].map(rep => `${base}-rep${rep}`).join(',');
      window.open(`/chip/${chipId}?samples=${samplesParam}&ggroups=${assay}`, '_blank');
    },
    [isAdminUser, sampleById],
  );

  const { groupLeftMargin, itemWidth } = getGroupItemWidthAndMargin(groupWidth, groupLength, currentGroupLength);
  const keys = isHeatmap ? getBarDatumKeys(datum) : geneGroups;
  // This checks whether the chart can fit the gene names next to it, in the whitespace.
  // totalGroupSize * 145 is the scaling width of the gene names, and window.innerWidth - 430 is the starting size.
  const fitsInChart = totalGroupSize * 177 < window.innerWidth - 458;
  const isLastAndTimed = againstTime && i === totalGroupSize - 1;
  const showGeneNames =
    isHeatmap && singleGeneHeatBoxHeight === 10 && fitsInChart && j === bioRepCount - 1 && !isLastAndTimed;

  const selectedEnvironmentIdsOrdered = queryFilters.filters.selectedEnvironmentIdsOrdered;
  const visualStatus = getVisualSelectionState(
    datum.environmentId,
    selectedEnvironmentIdsOrdered,
    samplesBeingSelected,
    environmentsForSelectedEnvTypeGroup.map(e => e.id),
  );

  const onClick = useCallback(
    (envId: number, keyPresses: KeysPressOptions) =>
      getSampleSelector(
        selectedEnvironmentIdsOrdered,
        environmentsForSelectedEnvTypeGroup.map(e => e.id),
        samplesBeingSelected,
        setSamplesBeingSelected,
        queryFilters.toggleEnvironmentStable,
        true,
        true,
      )(envId, keyPresses),
    [
      selectedEnvironmentIdsOrdered,
      environmentsForSelectedEnvTypeGroup,
      samplesBeingSelected,
      setSamplesBeingSelected,
      queryFilters.toggleEnvironmentStable,
    ],
  );

  const getBarColor = useCallback(
    (defaultColor: string) => {
      return visualStatus === VisualSelectionStatus.DESELECTED ? getSemiTransparentColor(defaultColor) : defaultColor;
    },
    [visualStatus],
  );
  const barOpacity = visualStatus !== VisualSelectionStatus.SELECTED ? 0.5 : 1;

  return (
    <>
      <BarStack<BarDatum, string>
        data={[datum]}
        keys={keys}
        x={_ => undefined /* calculated below */}
        xScale={xScale}
        yScale={yScale}
        color={barKey => getBarColor(colorScale(barKey))}
        opacity={barOpacity}
      >
        {stackRectangles => {
          return stackRectangles.map((rectangle, index) =>
            rectangle.bars.map(stackDatum => (
              <Fragment key={`brect${rectangle.index}-${rectangle.key}`}>
                {/* This is a rectangle that is full height and resides under the other rects. It contains a tooltip 
              with total number of detected genes, making empty samples more noticable */}
                {index === 0 && !isHeatmap && (
                  <BarRect
                    {...props}
                    height={277}
                    y={-1}
                    groupLeftMargin={groupLeftMargin}
                    itemWidth={itemWidth}
                    stackDatum={stackDatum}
                    rectIdx={sortIdxByGene[rectangle.key]}
                    color="transparent"
                    general={true}
                  />
                )}
                <BarRect
                  {...props}
                  height={isHeatmap ? singleGeneHeatBoxHeight : stackDatum.height}
                  groupLeftMargin={groupLeftMargin}
                  itemWidth={itemWidth}
                  stackDatum={stackDatum}
                  rectIdx={sortIdxByGene[rectangle.key]}
                  onClick={e => {
                    if (isHeatmap && isAdminUser) {
                      // Translate the key (which may be a gene, antibiotic group, etc.) into its corresponding
                      // assay identifier (e.g. "AY1234") so that the chip view receives a valid parameter.
                      const assay = getGroup(rectangle.key, 'assay') ?? rectangle.key;

                      if (assay.startsWith('AY')) {
                        openChip(Number(stackDatum.bar.data.sampleId), assay);
                        return;
                      }
                    }
                    // Fallback: toggle selection behaviour when not opening the chip view.
                    onClick(datum.environmentId, getKeyPressesFromMouseClick(e));
                  }}
                />
              </Fragment>
            )),
          );
        }}
      </BarStack>
      {showGeneNames && (
        <BarStack<BarDatum, string>
          data={[datum]}
          keys={keys}
          x={_ => undefined /* calculated below */}
          xScale={xScale}
          yScale={yScale}
          color={barKey => getBarColor(colorScale(barKey))}
          opacity={barOpacity}
        >
          {stackRectangles =>
            stackRectangles.map(rectangle =>
              rectangle.bars.map(stackDatum => (
                <BarRectName
                  key={`brect${rectangle.index}-${rectangle.key}`}
                  {...props}
                  height={singleGeneHeatBoxHeight}
                  groupLeftMargin={groupLeftMargin}
                  itemWidth={itemWidth}
                  stackDatum={stackDatum}
                  rectIdx={sortIdxByGene[rectangle.key]}
                  bioRepCount={bioRepCount}
                />
              )),
            )
          }
        </BarStack>
      )}
    </>
  );
}

interface BarRectProps extends GeneBarStackProps {
  groupLeftMargin: number;
  itemWidth: number;
  stackDatum: BarTooltipData;
  rectIdx: number;
  grouping: GeneGrouping;
  height: number;
  y?: number;
  color?: string;
  general?: boolean;
  onClick?: (e: React.MouseEvent<SVGRectElement>) => void;
}

function BarRect(props: BarRectProps) {
  const {
    height,
    rectIdx,
    isHeatmap,
    grouping,
    againstTime,
    i,
    j,
    xScale,
    colorScale,
    ordinalAligmentFixup,
    groupLeftMargin,
    groupWidth,
    itemWidth,
    stackDatum,
    tooltipStuff,
    color,
    y,
    general,
    onClick,
  } = props;
  const { allAssays, getGroup } = useAssayContext();
  const { queryFilters } = useSampleDataContext();
  const { samplesBeingSelected, environmentsForSelectedEnvTypeGroup, adminFeatures } = useResearchContext();
  const { tooltipData, tooltipProps, handleMouseMove } = usePlotTooltip<BarTooltipData>(tooltipStuff);
  const normalisationMode = useNormalisationMode();

  const visualStatus = getVisualSelectionState(
    stackDatum.bar.data.environmentId,
    queryFilters.filters.selectedEnvironmentIdsOrdered,
    samplesBeingSelected,
    environmentsForSelectedEnvTypeGroup.map(e => e.id),
  );

  const scaledX = againstTime
    ? (xScale as ScaleTime)(new Date(new Date(stackDatum.bar.data.date)))
    : (xScale as ScaleBand)(`${i}`);
  const yUpper = y || (isHeatmap ? rectIdx * height : stackDatum.y);
  const x = ordinalAligmentFixup + groupLeftMargin + j * itemWidth + scaledX - groupWidth / 2;
  const localColor = color || (isHeatmap ? binColor(getAbundace(stackDatum), normalisationMode) : stackDatum.color);

  const finalFillColor =
    visualStatus === VisualSelectionStatus.DESELECTED ? getSemiTransparentColor(localColor) : localColor;
  const finalOpacity = visualStatus !== VisualSelectionStatus.SELECTED ? 0.5 : 1;

  const shouldDrawFlagRectangle = useMemo(() => {
    if (!isHeatmap || !adminFeatures.highlightFlag || !adminFeatures.isModifyAsAdminActive) return false;
    const assay = getGroup(stackDatum.key, 'assay') ?? stackDatum.key;
    const cts = stackDatum.bar.data.ctsByAssay?.[assay] || [];
    return cts.some(ct => ct.flags?.split(/[,;\s]+/).includes(adminFeatures.highlightFlag as string));
  }, [isHeatmap, adminFeatures.highlightFlag, adminFeatures.isModifyAsAdminActive, stackDatum, getGroup]);

  return (
    <>
      <rect
        x={x}
        y={yUpper}
        height={height}
        width={itemWidth}
        fill={finalFillColor}
        opacity={finalOpacity}
        onMouseLeave={event => {
          handleMouseMove(event, undefined);
        }}
        onMouseMove={event => {
          if (queryFilters.filters.selectedTargetGrouping === 'assay') {
            const assay = allAssays.find(a => a.assay === stackDatum.key);
            if (assay) {
              handleMouseMove(event, {
                ...stackDatum,
                label: allAssays.find(a => a.assay === assay.assay)?.gene,
              });
            }
          } else {
            handleMouseMove(event, stackDatum);
          }
        }}
        onMouseEnter={event => {
          handleMouseMove(event, stackDatum, true);
        }}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        onClick={onClick}
      />
      {shouldDrawFlagRectangle && (
        <rect
          x={x}
          y={yUpper}
          height={height}
          width={itemWidth}
          fill="none"
          stroke="#000"
          strokeWidth={1}
          strokeOpacity={visualStatus === VisualSelectionStatus.DESELECTED ? 0.1 : 1}
          pointerEvents="none"
          shapeRendering="crispEdges"
        />
      )}
      {tooltipData && (
        <PlotTooltip {...tooltipProps}>
          {isHeatmap ? (
            <HeatmapTooltipContent grouping={grouping} tooltipData={tooltipData} colorScale={colorScale} />
          ) : (
            <NumDetectedTooltipContent
              tooltipData={tooltipData}
              colorScale={colorScale}
              grouping={grouping}
              showGeneInfo={!general}
            />
          )}
        </PlotTooltip>
      )}
    </>
  );
}

function BarRectName(props: BarRectProps) {
  const {
    height,
    rectIdx,
    isHeatmap,
    againstTime,
    i,
    xScale,
    ordinalAligmentFixup,
    groupLeftMargin,
    groupWidth,
    itemWidth,
    stackDatum,
    y,
  } = props;
  const scaledX = againstTime
    ? (xScale as ScaleTime)(new Date(new Date(stackDatum.bar.data.date)))
    : (xScale as ScaleBand)(`${i}`);
  const yUpper = y || (isHeatmap ? rectIdx * height : stackDatum.y);
  const x = ordinalAligmentFixup + groupLeftMargin + scaledX - groupWidth / 2 + 51;

  if (stackDatum.key === 'sampleNumber') {
    return null;
  }
  return (
    <text x={x} y={yUpper + 7} height={height} width={itemWidth} fill={theme.colors.neutral500} fontSize={10}>
      {stackDatum.key}
    </text>
  );
}
